import { $ } from 'jquery';
import { lay } from '../core/lay.js';
import { layer } from './layer.js';
import { i18n } from '../core/i18n.js';
import { componentBuilder } from '../core/component.js';

i18n.set({
  messages: {
    'zh-CN': {
      country: {
        country: '国家',
        province: '省份',
        city: '城市',
        district: '区县',
        county: '县区',
        town: '乡镇',
        village: '村镇',
      },
    },
    'en-US': {
      country: {
        country: 'Country',
        province: 'Province',
        city: 'City',
        district: 'District',
        county: 'County',
        town: 'Town',
        village: 'Village',
      },
    },
  },
});

var LEVEL_KEYS = ['country', 'province', 'city', 'district', 'community'];

var escapeHtml = function (value) {
  return value == null
    ? ''
    : String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
};

var trimText = function (value) {
  return value == null ? '' : String(value).trim();
};

var splitValues = function (value, delimiter) {
  var text = trimText(value);
  if (!text) {
    return [];
  }

  var list = text.split(delimiter || ',');
  var result = [];

  for (var i = 0; i < list.length; i++) {
    var item = trimText(list[i]);
    if (item !== '') {
      result.push(item);
    }
  }

  return result;
};

var toSelector = function (elem) {
  if (typeof elem !== 'string') {
    return elem;
  }

  if (/^[#.[]/.test(elem)) {
    return elem;
  }

  return '#' + elem;
};

var cloneList = function (list) {
  return (list || []).slice();
};

var findItemById = function (list, id) {
  var targetId = String(id);

  for (var i = 0; i < (list || []).length; i++) {
    if (String(list[i].id) === targetId) {
      return list[i];
    }
  }

  return null;
};

var buildDefaultLabels = function () {
  return [
    i18n.translate('country.country', null, { default: '国家' }),
    i18n.translate('country.province', null, { default: '省份' }),
    i18n.translate('country.city', null, { default: '城市' }),
    i18n.translate('country.county', null, { default: '县区' }),
    i18n.translate('country.town', null, { default: '乡镇' }),
  ];
};

var createInitialState = function (options) {
  var paths = splitValues(options.value, ',');
  var titles = splitValues(options.value_title, ',');
  var selectedValues = [];
  var selectedTitles = [];

  if (options.multiple) {
    for (var i = 0; i < paths.length; i++) {
      selectedValues.push(paths[i]);
      selectedTitles.push(titles[i] || paths[i]);
    }
  } else if (paths[0]) {
    selectedValues.push(paths[0]);
    selectedTitles.push(titles[0] || paths[0]);
  }

  return {
    cache: {},
    requests: {},
    topItems: [],
    currentPathIds: [],
    currentPathTitles: [],
    currentLevel: 1,
    maxLevel: 1,
    selectedValues: selectedValues,
    selectedTitles: selectedTitles,
    panelPrefixes: {},
  };
};

var component = componentBuilder({
  name: 'countryPicker',

  config: {
    elem: '',
    name: '',
    url: '',
    value: '',
    value_title: '',
    multiple: false,
    end: false,
    level: 5,
    txt: '',
    callback: function () {},
  },

  beforeRender: function (options) {
    options.level = parseInt(options.level, 10) || 5;
    options.level = Math.max(1, Math.min(options.level, LEVEL_KEYS.length));
    options.txt = options.txt || '';
    this.state = createInitialState(options);
  },
});

var Class = component.Class;

Class.prototype.getLevelKey = function (level) {
  return LEVEL_KEYS[level - 1];
};

Class.prototype.getDefaultLabel = function (level) {
  return buildDefaultLabels()[level - 1] || '';
};

Class.prototype.getPanelElem = function (level) {
  return this.panels[this.getLevelKey(level)];
};

Class.prototype.getValuePath = function (pathIds) {
  return cloneList(pathIds).join('/');
};

Class.prototype.getTitlePath = function (pathTitles) {
  return cloneList(pathTitles).join('/');
};

Class.prototype.isSelectedPath = function (value) {
  return this.state.selectedValues.indexOf(String(value)) !== -1;
};

Class.prototype.getRestorePathIds = function () {
  if (this.state.currentPathIds.length) {
    return cloneList(this.state.currentPathIds);
  }

  if (this.state.selectedValues.length) {
    return splitValues(this.state.selectedValues[0], '/').slice(
      0,
      this.config.level,
    );
  }

  return [];
};

Class.prototype.clearPanelsFrom = function (level) {
  for (var i = level; i <= this.config.level; i++) {
    delete this.state.panelPrefixes[i];
    this.getPanelElem(i).empty().hide();
  }

  if (level <= 1) {
    this.state.currentPathIds = [];
    this.state.currentPathTitles = [];
    this.state.currentLevel = 1;
    this.state.maxLevel = 1;
    return;
  }

  this.state.currentPathIds.splice(level - 1, LEVEL_KEYS.length);
  this.state.currentPathTitles.splice(level - 1, LEVEL_KEYS.length);
  this.state.currentLevel = Math.min(this.state.currentLevel, level - 1);
  this.state.maxLevel = Math.min(this.state.maxLevel, level - 1);
};

Class.prototype.normalizeResultList = function (data) {
  if ($.isArray(data)) {
    return data;
  }
  return [];
};

Class.prototype.fetchChildren = function (pid) {
  var that = this;
  var state = that.state;
  var key = String(pid);

  if (state.cache[key]) {
    return Promise.resolve(state.cache[key]);
  }

  if (state.requests[key]) {
    return state.requests[key];
  }

  state.requests[key] = new Promise(function (resolve) {
    var loadingIndex = layer.load(1);

    $.ajax({
      url: that.config.url,
      type: 'get',
      dataType: 'json',
      data: { pid: pid },
      success: function (result) {
        layer.close(loadingIndex);
        if (result && result.code && result.msg) {
          layer.msg(result.msg);
        }

        state.cache[key] = that.normalizeResultList(result && result.data);
        resolve(state.cache[key]);
      },
      error: function () {
        layer.close(loadingIndex);
        state.cache[key] = [];
        resolve(state.cache[key]);
      },
    });
  }).then(function (list) {
    delete state.requests[key];
    return list;
  });

  return state.requests[key];
};

Class.prototype.buildItemHtml = function (item, level, pathIds, pathTitles) {
  var nextPathIds = cloneList(pathIds);
  var nextPathTitles = cloneList(pathTitles);
  var itemId = String(item.id);
  var itemTitle = trimText(item.area);
  var hasChildren = parseInt(item.sub, 10) > 0;

  nextPathIds[level - 1] = itemId;
  nextPathIds.splice(level, LEVEL_KEYS.length);
  nextPathTitles[level - 1] = itemTitle;
  nextPathTitles.splice(level, LEVEL_KEYS.length);

  var valuePath = this.getValuePath(nextPathIds);
  var titlePath = this.getTitlePath(nextPathTitles);
  var selectedClass = !this.config.end && this.isSelectedPath(valuePath) ? ' active' : '';
  var labelClass = this.config.end && this.isSelectedPath(valuePath) ? ' active' : '';

  var html = '<span>';

  if (this.config.end) {
    html +=
      '<label class="layui-icon layui-icon-ok' +
      labelClass +
      '" data-id="' +
      escapeHtml(itemId) +
      '" data-level="' +
      level +
      '" data-title="' +
      escapeHtml(itemTitle) +
      '" data-sub="' +
      escapeHtml(item.sub) +
      '" data-value-path="' +
      escapeHtml(valuePath) +
      '" data-title-path="' +
      escapeHtml(titlePath) +
      '"></label>';
  }

  html +=
    '<a class="' +
    selectedClass +
    '" data-id="' +
    escapeHtml(itemId) +
    '" data-level="' +
    level +
    '" data-title="' +
    escapeHtml(itemTitle) +
    '" data-sub="' +
    escapeHtml(item.sub) +
    '" data-value-path="' +
    escapeHtml(valuePath) +
    '" data-title-path="' +
    escapeHtml(titlePath) +
    '" title="' +
    escapeHtml(itemTitle) +
    '">' +
    escapeHtml(itemTitle) +
    '</a>';

  html += '</span>';
  return html;
};

Class.prototype.renderCountryPanel = function (items) {
  var that = this;
  var groups = {};
  var groupOrder = [];
  var activeGroup = '';
  var selectedRootId = that.state.currentPathIds[0] || '';

  for (var i = 0; i < items.length; i++) {
    var item = items[i];
    var groupName = trimText(item.group) || '#';

    if (!groups[groupName]) {
      groups[groupName] = [];
      groupOrder.push(groupName);
    }

    groups[groupName].push(item);

    if (!activeGroup && selectedRootId && String(item.id) === String(selectedRootId)) {
      activeGroup = groupName;
    }
  }

  if (!activeGroup) {
    activeGroup = groupOrder[0] || '';
  }

  var html = '<dl><dt>';

  for (i = 0; i < groupOrder.length; i++) {
    html +=
      '<a data-group="' +
      escapeHtml(groupOrder[i]) +
      '"' +
      (groupOrder[i] === activeGroup ? ' class="active"' : '') +
      '>' +
      escapeHtml(groupOrder[i]) +
      '</a>';
  }

  html += '</dt>';

  for (i = 0; i < groupOrder.length; i++) {
    var groupKey = groupOrder[i];
    html +=
      '<dd data-group-panel="' +
      escapeHtml(groupKey) +
      '"' +
      (groupKey === activeGroup ? ' style="display: block"' : '') +
      '>';

    for (var j = 0; j < groups[groupKey].length; j++) {
      html += that.buildItemHtml(groups[groupKey][j], 1, [], []);
    }

    html += '</dd>';
  }

  html += '<div class="g3ys-region-clear"></div></dl>';
  that.panels.country.html(html);
};

Class.prototype.renderLevelPanel = function (level, items, pathIds, pathTitles) {
  var html = '<div>';

  for (var i = 0; i < items.length; i++) {
    html += this.buildItemHtml(items[i], level, pathIds, pathTitles);
  }

  html += '</div>';
  this.state.panelPrefixes[level] = {
    ids: cloneList(pathIds),
    titles: cloneList(pathTitles),
  };
  this.getPanelElem(level).html(html);
};

Class.prototype.syncGroupPanels = function () {
  var activeGroup = '';
  var currentRootId = this.state.currentPathIds[0] || '';

  if (currentRootId) {
    var currentRootItem = findItemById(this.state.topItems, currentRootId);
    activeGroup = currentRootItem ? trimText(currentRootItem.group) || '#' : '';
  }

  if (!activeGroup) {
    activeGroup =
      trimText(this.root.find('.g3ys-country dt a.active').data('group')) || '';
  }

  if (!activeGroup) {
    activeGroup = trimText(this.root.find('.g3ys-country dt a:first').data('group'));
  }

  if (!activeGroup) {
    return;
  }

  this.root.find('.g3ys-country dt a').removeClass('active');
  this.root
    .find('.g3ys-country dt a[data-group="' + activeGroup + '"]')
    .addClass('active');
  this.root.find('.g3ys-country dd').hide();
  this.root
    .find('.g3ys-country dd[data-group-panel="' + activeGroup + '"]')
    .show();
};

Class.prototype.syncSelectionClasses = function () {
  var that = this;

  that.root.find('.g3ys-region-content a[data-id]').each(function () {
    var node = $(this);
    var level = parseInt(node.data('level'), 10) || 1;
    var itemId = String(node.data('id'));
    var valuePath = String(node.data('value-path'));
    var isCurrent = String(that.state.currentPathIds[level - 1] || '') === itemId;
    var isSelected = that.isSelectedPath(valuePath);

    node.toggleClass('active', that.config.end ? isCurrent : isCurrent || isSelected);
  });

  that.root.find('label.layui-icon-ok[data-value-path]').each(function () {
    var node = $(this);
    node.toggleClass('active', that.isSelectedPath(String(node.data('value-path'))));
  });

  that.syncGroupPanels();
};

Class.prototype.showPanel = function (level) {
  var key = this.getLevelKey(level);

  $.each(this.panels, function (_, panel) {
    panel.hide();
  });

  this.panels[key].show();
  this.state.currentLevel = level;
  this.state.maxLevel = Math.max(this.state.maxLevel, level);
  this.updateTabs();
};

Class.prototype.updateTabs = function () {
  var that = this;

  for (var level = 1; level <= that.config.level; level++) {
    var tab = that.tabs[level - 1];
    var label = that.state.currentPathTitles[level - 1] || that.getDefaultLabel(level);

    tab.find('ins').text(label);

    if (level <= that.state.maxLevel) {
      tab.show();
    } else {
      tab.hide();
    }

    tab.toggleClass('active', level === that.state.currentLevel);
  }
};

Class.prototype.syncHiddenValue = function () {
  if (this.config.multiple) {
    this.hiddenInput.val(this.state.selectedValues.join(','));
  } else {
    this.hiddenInput.val(this.state.selectedValues[0] || '');
  }
};

Class.prototype.renderValueTags = function () {
  var that = this;
  var html = '';

  if (!that.state.selectedTitles.length) {
    that.valueSelect.html(escapeHtml(that.config.txt));
    that.syncHiddenValue();
    return;
  }

  for (var i = 0; i < that.state.selectedTitles.length; i++) {
    html +=
      '<div class="g3ys-region-value-item" data-value="' +
      escapeHtml(that.state.selectedValues[i]) +
      '">' +
      escapeHtml(that.state.selectedTitles[i]) +
      '<i class="layui-icon layui-icon-close"></i></div>';
  }

  that.valueSelect.html(html);
  that.syncHiddenValue();
};

Class.prototype.triggerCallback = function () {
  if (typeof this.config.callback !== 'function') {
    return;
  }

  if (this.config.multiple) {
    this.config.callback(
      this.state.selectedValues.join(','),
      this.state.selectedTitles.join(','),
    );
  } else {
    this.config.callback(
      this.state.selectedValues[0] || '',
      this.state.selectedTitles[0] || '',
    );
  }
};

Class.prototype.closePanel = function () {
  this.panel.hide();
};

Class.prototype.openPanel = function () {
  this.panel.show();
  this.syncSelectionClasses();
};

Class.prototype.selectValue = function (valuePath, titlePath) {
  var state = this.state;

  if (this.config.multiple) {
    if (state.selectedValues.indexOf(valuePath) === -1) {
      state.selectedValues.push(valuePath);
      state.selectedTitles.push(titlePath);
    }
  } else {
    state.selectedValues = [valuePath];
    state.selectedTitles = [titlePath];
  }

  this.renderValueTags();
  this.triggerCallback();
  this.syncSelectionClasses();
};

Class.prototype.removeSelectedValue = function (valuePath) {
  var state = this.state;
  var index = state.selectedValues.indexOf(String(valuePath));

  if (index === -1) {
    return;
  }

  state.selectedValues.splice(index, 1);
  state.selectedTitles.splice(index, 1);
  this.renderValueTags();
  this.triggerCallback();
  this.syncSelectionClasses();
};

Class.prototype.clearSingleValue = function () {
  this.state.selectedValues = [];
  this.state.selectedTitles = [];
  this.renderValueTags();
  this.triggerCallback();
  this.syncSelectionClasses();
};

Class.prototype.applyPathState = function (valuePath, titlePath) {
  this.state.currentPathIds = splitValues(valuePath, '/');
  this.state.currentPathTitles = splitValues(titlePath, '/');
};

Class.prototype.restorePathPanels = function (pathIds) {
  var that = this;
  var restoreIds = cloneList(pathIds || []).slice(0, that.config.level);

  that.clearPanelsFrom(2);
  that.state.currentLevel = 1;
  that.state.maxLevel = 1;

  if (!restoreIds.length) {
    that.showPanel(1);
    that.syncSelectionClasses();
    return Promise.resolve();
  }

  var visit = function (level, items, prefixIds, prefixTitles) {
    var targetId = restoreIds[level - 1];
    var item = findItemById(items, targetId);

    if (!item) {
      return Promise.resolve();
    }

    var nextIds = cloneList(prefixIds);
    var nextTitles = cloneList(prefixTitles);
    nextIds[level - 1] = String(item.id);
    nextIds.splice(level, LEVEL_KEYS.length);
    nextTitles[level - 1] = trimText(item.area);
    nextTitles.splice(level, LEVEL_KEYS.length);

    that.state.currentPathIds = cloneList(nextIds);
    that.state.currentPathTitles = cloneList(nextTitles);
    that.state.maxLevel = Math.max(that.state.maxLevel, level);

    if (
      level >= restoreIds.length ||
      level >= that.config.level ||
      parseInt(item.sub, 10) <= 0
    ) {
      return Promise.resolve();
    }

    return that.fetchChildren(item.id).then(function (children) {
      if (!children.length) {
        return;
      }

      that.renderLevelPanel(level + 1, children, nextIds, nextTitles);
      return visit(level + 1, children, nextIds, nextTitles);
    });
  };

  return visit(1, that.state.topItems, [], []).then(function () {
    that.showPanel(Math.max(1, that.state.currentPathIds.length || 1));
    that.syncSelectionClasses();
  });
};

Class.prototype.finalizeSelection = function (valuePath, titlePath) {
  this.applyPathState(valuePath, titlePath);
  this.selectValue(valuePath, titlePath);

  if (!this.config.multiple) {
    this.closePanel();
  }
};

Class.prototype.handleNodeClick = function (node, isEndPick) {
  var that = this;
  var level = parseInt(node.data('level'), 10) || 1;
  var itemId = String(node.data('id'));
  var itemTitle = trimText(node.data('title'));
  var subCount = parseInt(node.data('sub'), 10) || 0;
  var valuePath = String(node.data('value-path'));
  var titlePath = String(node.data('title-path'));
  var shouldFinalize = level >= that.config.level || subCount <= 0 || isEndPick;

  that.applyPathState(valuePath, titlePath);
  that.clearPanelsFrom(level + 1);
  that.syncSelectionClasses();

  if (shouldFinalize) {
    that.finalizeSelection(valuePath, titlePath);
    that.showPanel(level);
    return;
  }

  that.fetchChildren(itemId).then(function (items) {
    if (!items.length) {
      that.finalizeSelection(valuePath, titlePath);
      that.showPanel(level);
      return;
    }

    that.renderLevelPanel(
      level + 1,
      items,
      splitValues(valuePath, '/'),
      splitValues(titlePath, '/'),
    );
    that.showPanel(level + 1);
    that.syncSelectionClasses();
  });
};

Class.prototype.bindEvents = function () {
  var that = this;
  var eventNs = '.countryPicker-' + that.index;
  var outsideCleanup = that.cache('outsideCleanup');

  that.root.off(eventNs);
  $(document).off(eventNs);

  if (typeof outsideCleanup === 'function') {
    outsideCleanup();
  }

  that.root.on('click' + eventNs, '.g3ys-region-value-item', function (event) {
    event.stopPropagation();
  });

  that.root.on('click' + eventNs, '.g3ys-region-value-item i', function (event) {
    event.stopPropagation();
    var valuePath = $(this).parent().data('value');

    if (that.config.multiple) {
      that.removeSelectedValue(valuePath);
    } else {
      that.clearSingleValue();
    }
  });

  that.trigger.on('click' + eventNs, function (event) {
    lay.stope(event);
    if (that.panel.is(':visible')) {
      that.closePanel();
    } else {
      that.openPanel();
    }
  });

  that.closeBtn.on('click' + eventNs, function (event) {
    lay.stope(event);
    that.closePanel();
  });

  that.root.on('click' + eventNs, '.g3ys-region-title a', function (event) {
    lay.stope(event);
    var tab = $(this);
    var level = parseInt(tab.data('level'), 10) || 1;
    if (!that.getPanelElem(level).children().length) {
      return;
    }
    that.showPanel(level);
  });

  that.root.on('click' + eventNs, '.g3ys-country dt a', function (event) {
    lay.stope(event);
    var group = $(this).data('group');
    that.root.find('.g3ys-country dt a').removeClass('active');
    $(this).addClass('active');
    that.root.find('.g3ys-country dd').hide();
    that.root.find('.g3ys-country dd[data-group-panel="' + group + '"]').show();
  });

  that.root.on('click' + eventNs, '.g3ys-region-content a', function (event) {
    lay.stope(event);
    that.handleNodeClick($(this), false);
  });

  that.root.on('click' + eventNs, '.g3ys-region-content label.layui-icon-ok', function (event) {
    lay.stope(event);
    that.handleNodeClick($(this), true);
  });

  that.cache(
    'outsideCleanup',
    lay.onClickOutside(that.root[0], function () {
      that.closePanel();
    }),
  );
};

Class.prototype.render = function () {
  var that = this;
  var elem = that.config.elem;
  var levelHtml = '';

  for (var i = 0; i < that.config.level; i++) {
    levelHtml +=
      '<a data-level="' +
      (i + 1) +
      '"' +
      (i === 0 ? ' class="active"' : '') +
      '><ins>' +
      escapeHtml(that.getDefaultLabel(i + 1)) +
      '</ins><i class="layui-icon layui-icon-triangle-d"></i></a>';
  }

  elem.html(
    '<div class="g3ys-region-div">' +
      '<div class="g3ys-region-value">' +
      '<input type="hidden" value="' +
      escapeHtml(that.config.multiple ? that.state.selectedValues.join(',') : that.state.selectedValues[0] || '') +
      '" name="' +
      escapeHtml(that.config.name) +
      '">' +
      '<div class="g3ys-region-value-select"></div>' +
      '<i class="layui-icon layui-icon-down"></i>' +
      '<i class="layui-icon layui-icon-up"></i>' +
      '</div>' +
      '<div class="g3ys-region">' +
      '<div class="g3ys-region-title">' +
      levelHtml +
      '<i class="layui-icon layui-icon-close"></i>' +
      '</div>' +
      '<div class="g3ys-region-content">' +
      '<div class="g3ys-country"></div>' +
      '<div class="g3ys-province"></div>' +
      '<div class="g3ys-city"></div>' +
      '<div class="g3ys-district"></div>' +
      '<div class="g3ys-community"></div>' +
      '</div>' +
      '</div>' +
      '</div>',
  );

  that.root = elem.find('.g3ys-region-div');
  that.trigger = that.root.find('.g3ys-region-value');
  that.valueSelect = that.root.find('.g3ys-region-value-select');
  that.hiddenInput = that.root.find('input[type="hidden"]');
  that.panel = that.root.find('.g3ys-region');
  that.closeBtn = that.root.find('.g3ys-region-title .layui-icon-close');
  that.tabs = that.root.find('.g3ys-region-title a');
  that.panels = {
    country: that.root.find('.g3ys-country'),
    province: that.root.find('.g3ys-province'),
    city: that.root.find('.g3ys-city'),
    district: that.root.find('.g3ys-district'),
    community: that.root.find('.g3ys-community'),
  };

  $.each(that.panels, function (_, panel) {
    panel.hide();
  });

  that.renderValueTags();
  that.updateTabs();
  that.bindEvents();

  that.fetchChildren(0).then(function (items) {
    that.state.topItems = items;
    that.renderCountryPanel(items);
    that.restorePathPanels(that.getRestorePathIds());
  });
};

function Country() {}

Country.prototype.render = function (options) {
  options = $.extend({}, options, {
    elem: toSelector(options.elem),
  });

  return component.render(options);
};

Country.render = function (options) {
  return new Country().render(options);
};

$.extend(Country, component);

export { Country as country };
